import "./User.css";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import isEqual from "lodash/isEqual";
import addicon from "../../images/icons/addicon.svg";
import BankAccountVerification from "./elements/Bank";
import { useUserProfiles, useWiseBanks, useTeams, useUserPendingInvitesCount } from "./userProfileQueries";
import { useSelector, useDispatch } from "react-redux";
import { RenderUserCTA } from "../../helpers/invoiceHelpers";
import dletImage from "../../images/singleinvoice/delete.svg";
import crssrImage from "../../images/singleinvoice/cross-red.svg";
import { setHeaderText } from "../../features/headerText/headerTextSlice";
import { setNotification } from "../../features/notification/notificationSlice.js";
import userCategory from "../../constants/userCategory";
import userTypeName from "../../constants/userType";
import { motion } from "framer-motion";
import { useContractTypes, useUserType } from "./UserDetails/UserProfile/useUserView.jsx";
import TruncateFilterFieldView from "../helpers/truncateFilterFieldView.js";
import { setAppLoading } from "../../features/appSettings/appSettingsSlice.js";
import { handleResendEmailsService } from "./services.js";
import Filters from "./Includes/Filters.jsx";
import RecentUsers from "./Includes/RecentUsers.jsx";
import { handleSubmitService } from "./services.js";

// Import extracted components
import { ErrorBoundary } from "../shared/ErrorBoundary";
import UseRenderMemberName from "./UserComponents/UseRenderMemberName";
import UserPagination from "./UserComponents/UserPagination";
import UserActionButtons from "./UserComponents/UserActionButtons";
import { useUserExport } from "./UserComponents/useUserExport";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [filter, setFilter] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [users, setUsers] = useState([]);
  const [first, setFirst] = useState(0);
  const [emails, setEmails] = useState([]);
  const [userType, setUserType] = useState("");
  const roleDropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMailLoading, setIsMailLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const userTypeDropdownRef = useRef(null);
  const emailInputRef = useRef(null);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [userFilter, setUserFilter] = useState("All");
  const [selectAllTeam, setSelectAllTeam] = useState(true);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const [isTeamModalDropdownOpen, setIsTeamModalDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userCategoryFilter, setUserCategoryFilter] = useState("All");
  const [selectAllCategory, setSelectAllCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() =>
    Object.entries(userCategory)
      .filter(([name]) => name === "Individual" || name === "Business")
      .map(([, value]) => value)
  );
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isCategoryModalDropdownOpen, setIsCategoryModalDropdownOpen] = useState(false);
  const [userCategorySearchTerm, setUserCategorySearchTerm] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("All");
  const [selectAllAccountType, setSelectAllAccountType] = useState(true);
  const [selectedAccountType, setSelectedAccountType] = useState([]);
  const [isAccountTypeDropdownOpen, setIsAccountTypeDropdownOpen] = useState(false);
  const [isAccountTypeModalDropdownOpen, setIsAccountTypeModalDropdownOpen] = useState(false);
  const [roleIdSearchTerm, setRoleIdSearchTerm] = useState("");
  const [statusModalDropdownOpen, setStatusModalDropdownOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isBulkImportLoading, setIsBulkImportLoading] = useState(false);
  const [userTypeDropdownOpen, setUserTypeDropdownOpen] = useState(false);
  const [bulkImportFileName, setBulkImportFileName] = useState("");
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [alreadySentEmailsList, setAlreadySentEmailsList] = useState([]);
  const [showingDropdownOpen, setShowingDropdownOpen] = useState(false);
  const teamDropdownRef = useRef(null);
  const teamModalDropdownRef = useRef(null);
  const userCategoryDropdownRef = useRef(null);
  const userCategoryModalDropdownRef = useRef(null);
  const accountTypeDropdownRef = useRef(null);
  const accountTypeModalDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const statusModalDropdownRef = useRef(null);
  const showingDropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isUpdating, setIsUpdating] = useState(false);
  const [componentError, setComponentError] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const { appSettings } = useSelector((state) => state.appSettings);
  const token = user?.token ?? null;

  useEffect(() => {
    dispatch(setHeaderText("My Team"));
  }, [dispatch]);

  const { data: userProfiles = [], refetch: refetchUserProfiles, isLoading: userProfileLoading, isError, error } = useUserProfiles(token);
  const { data: roleId = [], isLoading: roleLoading } = useUserType(token);
  const { data: userPendingInvitesCount = 0, isLoading: pendingInvitesCountLoading, refetch: refetchPendingInvitesCount } = useUserPendingInvitesCount(token);

  // When "all" is selected, send [] to backend (no ID filter = all records)
  // This prevents refetch when userProfiles/roleId loads and populates the ID arrays
  const effectiveUserIds = selectAllTeam ? [] : selectedTeams;
  const effectiveRoleIds = selectAllAccountType ? [] : selectedAccountType;

  // Use React Query hook for teams data
  const {
    data: teamsData,
    isLoading: teamsLoading,
    isError: teamsError,
    error: teamsErrorData,
    refetch: refetchTeams
  } = useTeams(
    token,
    currentPage,
    rowsPerPage,
    filter,
    effectiveUserIds,
    selectedCategory,
    effectiveRoleIds,
    selectedStatus
  );

  // Extract data from teamsData
  const allTeam = teamsData || {};
  const paginatedTeams = allTeam.users || [];
  const total = allTeam.total || 0;

  // Handle loading state from React Query
  // Only show loading when query is enabled and actually loading
  const isQueryEnabled = !!token && selectedCategory?.length > 0;
  // Only show loading if query is enabled AND actually loading AND we don't have data yet
  const shouldShowLoading = isQueryEnabled && teamsLoading && !teamsData;

  // Update isUpdating based on loading states
  // Only consider teamsLoading if query is enabled
  useEffect(() => {
    setIsUpdating(userProfileLoading || shouldShowLoading);
  }, [userProfileLoading, shouldShowLoading]);

  useEffect(() => {
    let mounted = true;
    const handleLoading = async () => {
      const isLoading = shouldShowLoading && !teamsData;
      if (mounted) await dispatch(setAppLoading(isLoading));
    };
    handleLoading();
    return () => { mounted = false; };
  }, [shouldShowLoading, teamsData, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilter(debouncedValue);
    }, 500);
    return () => { clearTimeout(handler); };
  }, [debouncedValue]);

  // React Query will automatically refetch when dependencies change
  // No need for manual refetching via useEffect

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTeams]);

  // Handle errors from React Query
  useEffect(() => {
    if (teamsError && teamsErrorData) {
      setComponentError(teamsErrorData);
      toast.error("Failed to fetch teams. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      console.error("Error fetching teams:", teamsErrorData);
    }
  }, [teamsError, teamsErrorData]);

  const prevUserProfilesRef = useRef();
  useEffect(() => {
    // Check if userProfiles has actually changed by comparing JSON strings
    const currentUserProfilesString = JSON.stringify(userProfiles.map((team) => team._id));
    const dataChanged = prevUserProfilesRef.current !== currentUserProfilesString;

    if (dataChanged || prevUserProfilesRef.current === null) {
      prevUserProfilesRef.current = currentUserProfilesString;
      setSelectedTeams(userProfiles.map((team) => team._id));
    }
  }, [userProfiles]);

  const prevRoleIdRef = useRef();
  useEffect(() => {
    // Check if roleId has actually changed by comparing JSON strings
    const currentRoleIdString = JSON.stringify(roleId.map((team) => team._id));
    const dataChanged = prevRoleIdRef.current !== currentRoleIdString;

    if (dataChanged || prevRoleIdRef.current === null) {
      prevRoleIdRef.current = currentRoleIdString;
      setSelectedAccountType(roleId.map((team) => team._id));
    }
  }, [roleId]);

  useEffect(() => {
    setSelectedCategory(userCategoryList.map((item) => item.value));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // isUpdating is now managed by the combined loading states below

  const {
    data: typeContract = [],
    isLoading: contractTypesLoading,
    isError: contractTypesError,
  } = useContractTypes();

  const {
    data: wiseBanks = [],
    refetch: refetchWiseBanks,
    isLoading: wiseBanksLoading,
    isError: wiseBanksError,
    error: wiseBanksErrorMessage,
  } = useWiseBanks(token, paginatedTeams.length > 0);

  const prevUsersRef = useRef();
  useEffect(() => {
    // Check if userProfiles has actually changed by comparing JSON strings
    const currentUserProfilesString = JSON.stringify(userProfiles);
    const dataChanged = prevUsersRef.current !== currentUserProfilesString;

    if (dataChanged && userProfiles) {
      prevUsersRef.current = currentUserProfilesString;
      setUsers(userProfiles);
    }
  }, [userProfiles]);

  const renderUserStatus = (rowData) => {
    let statusClass;
    let statusName;
    if (rowData.is_active === true) {
      statusClass = "active";
      statusName = "Active";
    } else if (rowData.is_active === false) {
      statusClass = "inactive";
      statusName = "Inactive";
    }
    return (
      <span className={`payment-status ${statusClass}`}>{statusName}</span>
    );
  };
  const [emailValidation, setEmailValodation] = useState(false);
  const [selectValidation, setSelectValodation] = useState(false);

  const handleOpenResendModal = useCallback(() => {
    setIsResendModalOpen(true);
  }, []);

  const handleCloseResendModal = useCallback(() => {
    setIsResendModalOpen(false);
    setAlreadySentEmailsList([]);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEmails([]);
    setEmail("");
    setUserType("");
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    await handleSubmitService({ handleOpenResendModal, dispatch, setNotification, setAlreadySentEmailsList, setIsModalOpen, email, userType, emails, setEmail, setEmails, setEmailValodation, setSelectValodation, toast, handleCloseModal, refetchPendingInvitesCount, setIsMailLoading, setComponentError, token });
  }, [handleOpenResendModal, dispatch, setAlreadySentEmailsList, setIsModalOpen, email, userType, emails, setEmail, setEmails, setEmailValodation, setSelectValodation, handleCloseModal, refetchPendingInvitesCount, setIsMailLoading, setComponentError, token]);

  const handleResendEmails = useCallback(async () => {
    await handleResendEmailsService(alreadySentEmailsList, token, setIsMailLoading, setIsResendModalOpen, toast, global, refetchPendingInvitesCount, handleCloseResendModal, setComponentError);
  }, [alreadySentEmailsList, token, setIsMailLoading, setIsResendModalOpen, refetchPendingInvitesCount, handleCloseResendModal, setComponentError]);

  const handleAddEmail = useCallback((e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmails = emailRegex.test(email);
    if (email && !isValidEmails) {
      toast.error("Please enter valid email addresses!!", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    const newEmailEntry = { email: email, id: Date.now(), userType: userType };
    setEmails([...emails, newEmailEntry]);
    setEmail("");
    setUserType("");
  }, [email, userType, emails]);

  const handleToggleRoleDropdown = useCallback(() => {
    setRoleDropdownOpen(!roleDropdownOpen);
  }, [roleDropdownOpen]);

  const handleToggleUserTypeDropdown = useCallback(() => {
    setUserTypeDropdownOpen(!userTypeDropdownOpen);
  }, [userTypeDropdownOpen]);

  const handleUserTypeOptionClick = useCallback((selectedValue) => {
    setUserType(selectedValue);
    setSelectValodation(false);
    setUserTypeDropdownOpen(false);
  }, [setSelectValodation]);

  const handleCheckboxTeamChange = useCallback((teamId) => {
    const updatedSelection = [...selectedTeams];
    const index = updatedSelection.indexOf(teamId);

    if (index === -1) {
      updatedSelection.push(teamId);
    } else {
      updatedSelection.splice(index, 1);
    }
    setFirst(0);
    setSelectedTeams(updatedSelection);
  }, [selectedTeams]);

  const handleSelectAllTeamChange = useCallback(() => {
    if (!selectAllTeam) {
      setSelectedTeams(userProfiles.map((team) => team._id));
    } else {
      setSelectedTeams([]);
    }
    setSelectAllTeam(!selectAllTeam);
  }, [selectAllTeam, userProfiles]);

  const filteredUsers = useMemo(() => {
    return userProfiles.filter((user) => {
      const fullName = user.first_name
        ? `${user.first_name} ${user.middle_name || ""} ${user.last_name}`
        : `${user.company_first_name ? user.company_first_name + (user.company_last_name && user.company_last_name) : user.company_name}`;
      return fullName.toLowerCase().includes(userSearchTerm.toLowerCase());
    });
  }, [userProfiles, userSearchTerm]);

  const handleUserSearchChange = useCallback((e) => {
    setUserSearchTerm(e.target.value);
  }, []);

  const toggleTeamDropdown = useCallback(() => {
    setIsTeamDropdownOpen(!isTeamDropdownOpen);
    setUserSearchTerm("");
  }, [isTeamDropdownOpen]);

  const toggleTeamModalDropdown = useCallback(() => {
    setIsTeamModalDropdownOpen(!isTeamModalDropdownOpen);
    setUserSearchTerm("");
  }, [isTeamModalDropdownOpen]);

  const userCategoryList = Object.entries(userCategory)
    .filter(([name]) => name === "Individual" || name === "Business")
    .map(([name, value]) => ({
      name,
      value,
    }));

  const filteredUserCategoryList = useMemo(() => {
    return userCategoryList && userCategoryList.filter((userCategory) =>
      userCategory && userCategory.name
        ?.toLowerCase()
        .includes(userCategorySearchTerm?.toLowerCase())
    );
  }, [userCategoryList, userCategorySearchTerm]);

  const handleUserCategorySearchChange = useCallback((e) => {
    setUserCategorySearchTerm(e.target.value);
  }, []);

  const toggleCategoryDropdown = useCallback(() => {
    setUserCategorySearchTerm("");
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  }, [isCategoryDropdownOpen]);

  const toggleCategoryModalDropdown = useCallback(() => {
    setUserCategorySearchTerm("");
    setIsCategoryModalDropdownOpen(!isCategoryModalDropdownOpen);
  }, [isCategoryModalDropdownOpen]);

  const handleCheckboxCategoryChange = useCallback((value) => {
    setSelectedCategory((prevSelected) =>
      prevSelected.includes(value)
        ? prevSelected.filter((item) => item !== value)
        : [...prevSelected, value]
    );
  }, []);

  const handleSelectAllCategoryChange = useCallback(() => {
    setSelectAllCategory(!selectAllCategory);
    if (!selectAllCategory) {
      setSelectedCategory(userCategoryList.map((item) => item.value));
    } else {
      setSelectedCategory([]);
    }
  }, [selectAllCategory, userCategoryList]);

  const prevSelectedCategoryRef = useRef();
  const prevUserCategoryListRef = useRef();
  useEffect(() => {
    // Check if selectedCategory or userCategoryList has actually changed
    const currentSelectedCategoryString = JSON.stringify(selectedCategory);
    const currentUserCategoryListString = JSON.stringify(userCategoryList.map((item) => item.value));
    const categoryChanged = prevSelectedCategoryRef.current !== currentSelectedCategoryString;
    const listChanged = prevUserCategoryListRef.current !== currentUserCategoryListString;

    if (categoryChanged || listChanged || prevSelectedCategoryRef.current === null) {
      prevSelectedCategoryRef.current = currentSelectedCategoryString;
      prevUserCategoryListRef.current = currentUserCategoryListString;

      setSelectAllCategory(selectedCategory.length === userCategoryList.length);
      if (selectedCategory.length === userCategoryList.length) {
        setUserCategoryFilter("All");
      } else {
        setUserCategoryFilter(selectedTeams);
      }
    }
  }, [selectedCategory, userCategoryList, selectedTeams]);

  const filteredRoleId = useMemo(() => {
    return roleId.filter(
      (role) =>
        !(
          user.role_id === userTypeName.Admin &&
          role._id === userTypeName.Super_Admin
        )
    );
  }, [roleId, user.role_id]);

  const filteredRoleIdList = useMemo(() => {
    return filteredRoleId && filteredRoleId.filter((accountType) =>
      accountType && accountType.user_type
        ?.toLowerCase()
        .includes(roleIdSearchTerm?.toLowerCase())
    );
  }, [filteredRoleId, roleIdSearchTerm]);

  const handleRoleIdSearchChange = useCallback((e) => {
    setRoleIdSearchTerm(e.target.value);
  }, []);

  const toggleAccountTypeDropdown = useCallback(() => {
    setIsAccountTypeDropdownOpen(!isAccountTypeDropdownOpen);
  }, [isAccountTypeDropdownOpen]);

  const toggleAccountTypeModalDropdown = useCallback(() => {
    setIsAccountTypeModalDropdownOpen(!isAccountTypeModalDropdownOpen);
  }, [isAccountTypeModalDropdownOpen]);

  const handleCheckboxAccountTypeChange = useCallback((value) => {
    const updatedSelection = [...selectedAccountType];
    const index = updatedSelection.indexOf(value);

    if (index === -1) {
      updatedSelection.push(value);
    } else {
      updatedSelection.splice(index, 1);
    }
    setSelectedAccountType(updatedSelection);
  }, [selectedAccountType]);

  const handleSelectAllAccountTypeChange = useCallback(() => {
    setSelectAllAccountType(!selectAllAccountType);
    if (!selectAllAccountType) {
      setSelectedAccountType(filteredRoleId.map((item) => item._id));
    } else {
      setSelectedAccountType([]);
    }
  }, [selectAllAccountType, filteredRoleId]);

  const toggleStatusDropdown = useCallback(() => {
    setStatusDropdownOpen(!statusDropdownOpen);
  }, [statusDropdownOpen]);

  const toggleStatusModalDropdown = useCallback(() => {
    setStatusModalDropdownOpen(!statusModalDropdownOpen);
  }, [statusModalDropdownOpen]);

  const selectStatusOption = useCallback((selectedStatus) => {
    setSelectedStatus(selectedStatus);
    setStatusDropdownOpen(false);
  }, []);

  const applyFilters = useCallback(() => {
    setShowFilterModal(false);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedTeams(userProfiles.map((team) => team._id));
    setSelectedCategory(userCategoryList.map((item) => item.value));
    setSelectedAccountType(filteredRoleId.map((type) => type._id));
    setSelectedStatus(null);
    setShowFilterModal(false);
  }, [userProfiles, userCategoryList, filteredRoleId]);

  const checkAndShowConfirmationModal = useCallback(() => {
    const isTeamsModified = !isEqual(
      selectedTeams,
      userProfiles.map((team) => team._id)
    );
    const isUserCategoryModified = !isEqual(
      selectedCategory,
      userCategoryList.map((item) => item.value)
    );
    const isAccountTypeModified = !isEqual(
      selectedAccountType,
      filteredRoleId.map((type) => type._id)
    );

    if (isTeamsModified || isUserCategoryModified || isAccountTypeModified) {
      setShowConfirmationModal(true);
    } else {
      setShowFilterModal(false);
    }
  }, [selectedTeams, userProfiles, selectedCategory, userCategoryList, selectedAccountType, filteredRoleId]);

  const handleCancel = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleApplyConfirm = useCallback(() => {
    setShowConfirmationModal(false);
    setShowFilterModal(false);
  }, []);

  const toggleFilterModal = useCallback(() => {
    setShowConfirmationModal(false);
    setShowFilterModal(!showFilterModal);
  }, [showFilterModal]);

  const handleClickOutsideDropdown = (event) => {
    const isClickInsideRoleDropdown =
      roleDropdownRef.current && roleDropdownRef.current.contains(event.target);
    const isClickInsideUserTypeDropdown =
      userTypeDropdownRef.current &&
      userTypeDropdownRef.current.contains(event.target);
    const isClickInsideTeamDropdown =
      teamDropdownRef.current && teamDropdownRef.current.contains(event.target);
    const isClickInsideTeamModalDropdown =
      teamModalDropdownRef.current &&
      teamModalDropdownRef.current.contains(event.target);
    const isClickInsideUserCategoryDropdown =
      userCategoryDropdownRef.current &&
      userCategoryDropdownRef.current.contains(event.target);
    const isClickInsideUserCategoryModalDropdown =
      userCategoryModalDropdownRef.current &&
      userCategoryModalDropdownRef.current.contains(event.target);
    const isClickInsideAccountTypeDropdown =
      accountTypeDropdownRef.current &&
      accountTypeDropdownRef.current.contains(event.target);
    const isClickInsideAccountTypeModalDropdown =
      accountTypeModalDropdownRef.current &&
      accountTypeModalDropdownRef.current.contains(event.target);
    const isClickInsideStatusDropdown =
      statusDropdownRef.current &&
      statusDropdownRef.current.contains(event.target);
    const isClickInsideStatusModalDropdown =
      statusModalDropdownRef.current &&
      statusModalDropdownRef.current.contains(event.target);
    const isClickInsideshowingDropdown =
      showingDropdownRef.current && showingDropdownRef.current.contains(event.target);

    if (!isClickInsideRoleDropdown) {
      setRoleDropdownOpen(false);
    }
    if (!isClickInsideUserTypeDropdown) {
      setUserTypeDropdownOpen(false);
    }
    if (!isClickInsideTeamDropdown) {
      setIsTeamDropdownOpen(false);
    }
    if (!isClickInsideTeamModalDropdown) {
      setIsTeamModalDropdownOpen(false);
    }
    if (!isClickInsideUserCategoryDropdown) {
      setIsCategoryDropdownOpen(false);
    }
    if (!isClickInsideUserCategoryModalDropdown) {
      setIsCategoryModalDropdownOpen(false);
    }
    if (!isClickInsideAccountTypeDropdown) {
      setIsAccountTypeDropdownOpen(false);
    }
    if (!isClickInsideAccountTypeModalDropdown) {
      setIsAccountTypeModalDropdownOpen(false);
    }
    if (!isClickInsideStatusDropdown) {
      setStatusDropdownOpen(false);
    }
    if (!isClickInsideStatusModalDropdown) {
      setStatusModalDropdownOpen(false);
    }
    if (!isClickInsideshowingDropdown) {
      setShowingDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("click", handleClickOutsideDropdown);
    };
  }, []);

  const handleRowsPerPageChange = useCallback((e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  }, []);

  const totalPages = Math.ceil(total / rowsPerPage);

  const handleEditEmail = useCallback((id, field, value) => {
    const index = emails.findIndex((obj) => obj.id === id);
    if (index !== -1) {
      const updatedEmails = [...emails];
      updatedEmails[index][field] = value;
      setEmails(updatedEmails);
    }
  }, [emails]);

  const handleRemoveEmail = useCallback((id) => {
    const filteredEmails = emails.filter((obj) => obj.id !== id);
    setEmails(filteredEmails);
  }, [emails]);

  const handleRemoveLatestEmail = useCallback(() => {
    if (emails.length === 0) return;
    const updatedEmails = [...emails];
    const lastEmail = updatedEmails.pop();
    setEmails(updatedEmails);
    setEmail(lastEmail.email);
    setUserType(lastEmail.userType);
  }, [emails]);

  const handleRemoveResendEmail = useCallback((emailToRemove) => {
    const updatedEmails = alreadySentEmailsList.filter(
      (email) => email !== emailToRemove
    );
    setAlreadySentEmailsList(updatedEmails);
  }, [alreadySentEmailsList]);

  const handleBulkImportCloseModal = useCallback(() => {
    setBulkImportFile(null);
    setBulkImportFileName("");
    setIsBulkImportModalOpen(false);
  }, []);

  const handleUpload = useCallback(async (event) => {
    event.preventDefault();
    setIsBulkImportLoading(true);
    if (!bulkImportFile) {
      toast.error("Please select a file.", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setIsBulkImportLoading(false);
      return;
    }
    const maxFileSize = 5 * 1024 * 1024;
    if (bulkImportFile.size > maxFileSize) {
      toast.error("File size exceeds the 5 MB limit.", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setIsBulkImportLoading(false);
      return;
    }
    const fileExtension = bulkImportFile.name.split(".").pop();
    if (fileExtension !== "xlsx") {
      toast.error("Only xlsx files are supported.", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setIsBulkImportLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append("file", bulkImportFile);
    try {
      const response = await axios.post("/api/import/user", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.status === "requiredValidationError") {
        toast.error(response.data.message, {
          position: "bottom-right",
          autoClose: 5000,
        });
      } else {
        if (
          response.data.existingUserEmails &&
          response.data.existingUserEmails.length > 0
        ) {
          toast.error(
            `The following emails are already registered: ${response.data.existingUserEmails.join(", ")}`,
            {
              position: "bottom-right",
              autoClose: 5000,
            }
          );
          if (
            response.data.filteredUsers &&
            response.data.filteredUsers.length > 0
          ) {
            toast.success(
              `Inserted ${response.data.filteredUsers.length} records`,
              {
                position: "bottom-right",
                autoClose: 5000,
              }
            );
          }
        } else {
          toast.success("File uploaded successfully!", {
            position: "bottom-right",
            autoClose: 1500,
          });
        }
        await Promise.all([refetchUserProfiles(), refetchTeams()]);
        handleBulkImportCloseModal();
      }
    } catch (error) {
      setComponentError(error);
      toast.error("Error uploading file. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      console.error("Error uploading file:", error);
      if (global.config?.slackMessage) {
        global.config.slackMessage(error.toString());
      }
    } finally {
      setIsBulkImportLoading(false);
    }
  }, [bulkImportFile, token, refetchUserProfiles, refetchTeams, handleBulkImportCloseModal, setComponentError]);

  const handleBulkImportUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setBulkImportFile(e.target.files[0]);
      setBulkImportFileName(file.name);
    } else {
      setBulkImportFile(null);
      setBulkImportFileName("");
    }
  }, []);

  const componentRef = useRef(null);
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  // Use export hook
  const { exportToExcel: exportToExcelHook } = useUserExport();

  // Helper function to build full name (memoized)
  const buildFullName = useCallback((item) => {
    const firstName = item.first_name ||
      (item.company_first_name
        ? `${item.company_first_name}${item.company_last_name ? ` ${item.company_last_name}` : ""}`
        : `${item.company_name} (Company)`);
    const middleName = item.middle_name && item.middle_name !== "null" ? ` ${item.middle_name}` : "";
    return `${firstName}${middleName} ${item.last_name}`;
  }, []);

  // Helper function to check search match (with early return)
  const checkSearchMatch = useCallback((item, searchTerm) => {
    if (!searchTerm) return true; // Early return if no search term

    const fullName = buildFullName(item).toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();

    return (
      fullName.includes(lowerSearchTerm) ||
      item.job_title?.toLowerCase().includes(lowerSearchTerm) ||
      item.email?.toLowerCase().includes(lowerSearchTerm) ||
      item.user_wx_comapny?.wx_company?.toLowerCase().includes(lowerSearchTerm)
    );
  }, [buildFullName]);

  // Helper function to check status match (with early return)
  const checkStatusMatch = useCallback((item) => {
    if (selectedStatus === "Active") {
      return item.is_active === true;
    }
    if (selectedStatus === "Inactive") {
      return item.is_active === false;
    }
    return true; // No status filter selected
  }, [selectedStatus]);

  // Helper function to check user filter match (with early return)
  const checkUserFilterMatch = useCallback((item) => {
    if (userFilter === "All") return true; // Early return
    return selectedTeams.includes(item._id);
  }, [userFilter, selectedTeams]);

  // Helper function to check category filter match (with early return)
  const checkCategoryFilterMatch = useCallback((item) => {
    if (userCategoryFilter === "All") return true; // Early return
    return selectedCategory.includes(item.user_category);
  }, [userCategoryFilter, selectedCategory]);

  // Helper function to check account type filter match (with early return)
  const checkAccountTypeFilterMatch = useCallback((item) => {
    if (accountTypeFilter === "All") return true; // Early return
    return selectedAccountType.includes(item?.role_id?._id);
  }, [accountTypeFilter, selectedAccountType]);

  // Helper function to check category type (Individual/Business)
  const checkCategoryType = useCallback((item) => {
    return (
      item.user_category === userCategory.Individual ||
      item.user_category === userCategory.Business
    );
  }, []);

  // Helper function to check role-based filters (with early return)
  const checkRoleBasedFilters = useCallback((item) => {
    if (user.role_id === userTypeName.Admin) {
      // Admin cannot see Super Admin
      if (item?.role_id?._id === userTypeName.Super_Admin) {
        return false; // Early return
      }
    }
    return true;
  }, [user.role_id]);

  // Memoize filtered data for export with optimized filtering
  const filteredData = useMemo(() => {
    const searchTerm = filter.trim();

    return users.filter((item) => {
      // Early return: Check search match first (most likely to fail)
      if (!checkSearchMatch(item, searchTerm)) {
        return false;
      }

      // Early return: Check status match
      if (!checkStatusMatch(item)) {
        return false;
      }

      // Early return: Check role-based filters
      if (!checkRoleBasedFilters(item)) {
        return false;
      }

      // Early return: Check category type (Individual/Business)
      if (!checkCategoryType(item)) {
        return false;
      }

      // Early return: Check user filter match
      if (!checkUserFilterMatch(item)) {
        return false;
      }

      // Early return: Check category filter match
      if (!checkCategoryFilterMatch(item)) {
        return false;
      }

      // Early return: Check account type filter match
      if (!checkAccountTypeFilterMatch(item)) {
        return false;
      }

      // All checks passed
      return true;
    });
  }, [
    users,
    filter,
    checkSearchMatch,
    checkStatusMatch,
    checkUserFilterMatch,
    checkCategoryFilterMatch,
    checkAccountTypeFilterMatch,
    checkCategoryType,
    checkRoleBasedFilters,
  ]);

  const exportToExcel = useCallback(() => {
    exportToExcelHook(filteredData);
  }, [filteredData, exportToExcelHook]);

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  if (componentError) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '40px 20px',
          backgroundColor: '#f9fafb',
        }}
      >
        <div
          style={{
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div
            style={{
              fontSize: '64px',
              marginBottom: '24px',
            }}
            aria-hidden="true"
          >
            ⚠️
          </div>
          <h2
            style={{
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#1f2937',
            }}
          >
            An Error Occurred
          </h2>
          <p
            style={{
              fontSize: '16px',
              color: '#4b5563',
              marginBottom: '32px',
              lineHeight: '1.6',
            }}
          >
            Something went wrong. Please try refreshing the page or contact support if the problem persists.
          </p>
          <button
            onClick={() => window.location.reload()}
            aria-label="Refresh page"
            style={{
              padding: '12px 32px',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <motion.main
        ref={componentRef}
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ duration: 0.3 }}
        role="main"
        aria-label="User overview and management"
      >
        <div className="user-outer-overview invoice-outer-invoice">
          <h2 className="my-team-mobile-page-title">My Team</h2>
          <div className="invoice-top">
            <div className="new-top-flter">
              <UserActionButtons
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                isMailLoading={isMailLoading}
                emails={emails}
                handleAddEmail={handleAddEmail}
                handleEditEmail={handleEditEmail}
                handleSubmit={handleSubmit}
                handleCloseModal={handleCloseModal}
                pendingInvitesCountLoading={pendingInvitesCountLoading}
                userPendingInvitesCount={userPendingInvitesCount}
                navigate={navigate}
                roleId={roleId}
                roleDropdownOpen={roleDropdownOpen}
                setRoleDropdownOpen={setRoleDropdownOpen}
                handleToggleRoleDropdown={handleToggleRoleDropdown}
                roleDropdownRef={roleDropdownRef}
                userTypeDropdownOpen={userTypeDropdownOpen}
                handleToggleUserTypeDropdown={handleToggleUserTypeDropdown}
                userTypeDropdownRef={userTypeDropdownRef}
                userType={userType}
                handleUserTypeOptionClick={handleUserTypeOptionClick}
                email={email}
                setEmail={setEmail}
                emailValidation={emailValidation}
                setEmailValodation={setEmailValodation}
                selectValidation={selectValidation}
                dletImage={dletImage}
                addicon={addicon}
                handleRemoveEmail={handleRemoveEmail}
                handleRemoveLatestEmail={handleRemoveLatestEmail}
                emailInputRef={emailInputRef}
                isResendModalOpen={isResendModalOpen}
                setIsResendModalOpen={setIsResendModalOpen}
                handleCloseResendModal={handleCloseResendModal}
                handleResendEmails={handleResendEmails}
                alreadySentEmailsList={alreadySentEmailsList}
                handleRemoveResendEmail={handleRemoveResendEmail}
                crssrImage={crssrImage}
                isBulkImportModalOpen={isBulkImportModalOpen}
                setIsBulkImportModalOpen={setIsBulkImportModalOpen}
                bulkImportFileName={bulkImportFileName}
                handleBulkImportUpload={handleBulkImportUpload}
                handleUpload={handleUpload}
                isBulkImportLoading={isBulkImportLoading}
                handleBulkImportCloseModal={handleBulkImportCloseModal}
                exportToExcel={exportToExcel}
              />
              <div className="search-sec search-sec-user-overview" role="search">
                <div className="search-container">
                  <label htmlFor="user-search-input" className="sr-only">
                    Search users
                  </label>
                  <input
                    id="user-search-input"
                    type="text"
                    onChange={(e) => {
                      setSearchFilter(e.target.value);
                      setDebouncedValue(e.target.value);
                      setCurrentPage(1);
                    }}
                    value={searchFilter}
                    className="search-input"
                    placeholder="Search"
                    aria-label="Search users by name, email, job title, or company"
                    aria-describedby="search-description"
                  />
                  <span id="search-description" className="sr-only">
                    Search for users by name, email, job title, or company name
                  </span>
                  <div aria-live="polite" aria-atomic="true" className="sr-only">
                    {searchFilter && `Searching for ${searchFilter}`}
                  </div>
                </div>
              </div>
            </div>

            <Filters
              selectedTeams={selectedTeams}
              userProfiles={userProfiles}
              teamDropdownRef={teamDropdownRef}
              toggleTeamDropdown={toggleTeamDropdown}
              TruncateFilterFieldView={TruncateFilterFieldView}
              isTeamDropdownOpen={isTeamDropdownOpen}
              userSearchTerm={userSearchTerm}
              handleUserSearchChange={handleUserSearchChange}
              filteredUsers={filteredUsers}
              selectAllTeam={selectAllTeam}
              handleSelectAllTeamChange={handleSelectAllTeamChange}
              handleCheckboxTeamChange={handleCheckboxTeamChange}

              userCategoryDropdownRef={userCategoryDropdownRef}
              toggleCategoryDropdown={toggleCategoryDropdown}
              selectedCategory={selectedCategory}
              userCategoryList={userCategoryList}
              isCategoryDropdownOpen={isCategoryDropdownOpen}
              userCategorySearchTerm={userCategorySearchTerm}
              handleUserCategorySearchChange={handleUserCategorySearchChange}
              filteredUserCategoryList={filteredUserCategoryList}
              selectAllCategory={selectAllCategory}
              handleSelectAllCategoryChange={handleSelectAllCategoryChange}
              handleCheckboxCategoryChange={handleCheckboxCategoryChange}

              accountTypeDropdownRef={accountTypeDropdownRef}
              toggleAccountTypeDropdown={toggleAccountTypeDropdown}
              selectedAccountType={selectedAccountType}
              filteredRoleId={filteredRoleId}
              isAccountTypeDropdownOpen={isAccountTypeDropdownOpen}
              roleIdSearchTerm={roleIdSearchTerm}
              handleRoleIdSearchChange={handleRoleIdSearchChange}
              filteredRoleIdList={filteredRoleIdList}
              selectAllAccountType={selectAllAccountType}
              handleSelectAllAccountTypeChange={handleSelectAllAccountTypeChange}
              handleCheckboxAccountTypeChange={handleCheckboxAccountTypeChange}

              statusDropdownRef={statusDropdownRef}
              toggleStatusDropdown={toggleStatusDropdown}
              selectedStatus={selectedStatus}
              appSettings={appSettings}
              statusDropdownOpen={statusDropdownOpen}
              selectStatusOption={selectStatusOption}

              toggleFilterModal={toggleFilterModal}
              Modal={Modal}
              showFilterModal={showFilterModal}
              checkAndShowConfirmationModal={checkAndShowConfirmationModal}

              userCategoryModalDropdownRef={userCategoryModalDropdownRef}
              accountTypeModalDropdownRef={accountTypeModalDropdownRef}
              toggleAccountTypeModalDropdown={toggleAccountTypeModalDropdown}
              isAccountTypeModalDropdownOpen={isAccountTypeModalDropdownOpen}
              teamModalDropdownRef={teamModalDropdownRef}
              toggleTeamModalDropdown={toggleTeamModalDropdown}
              isTeamModalDropdownOpen={isTeamModalDropdownOpen}
              isCategoryModalDropdownOpen={isCategoryModalDropdownOpen}
              statusModalDropdownRef={statusModalDropdownRef}
              toggleStatusModalDropdown={toggleStatusModalDropdown}
              toggleCategoryModalDropdown={toggleCategoryModalDropdown}
              statusModalDropdownOpen={statusModalDropdownOpen}

              showConfirmationModal={showConfirmationModal}
              handleCancel={handleCancel}
              handleApplyConfirm={handleApplyConfirm}
              clearFilters={clearFilters}
              applyFilters={applyFilters}
              userProfileLoading={userProfileLoading}
              roleLoading={roleLoading}
            />

            <UserPagination
              total={total}
              rowsPerPage={rowsPerPage}
              handleRowsPerPageChange={handleRowsPerPageChange}
              showingDropdownRef={showingDropdownRef}
              showingDropdownOpen={showingDropdownOpen}
              setShowingDropdownOpen={setShowingDropdownOpen}
            />
          </div>

          <RecentUsers
            wiseBanks={wiseBanks}
            wiseBanksLoading={wiseBanksLoading}
            wiseBanksError={wiseBanksError}
            paginatedTeams={paginatedTeams}
            isUpdating={isUpdating}
            RenderUserCTA={RenderUserCTA}
            renderUserStatus={renderUserStatus}
            UseRenderMemberName={UseRenderMemberName}
            BankAccountVerification={BankAccountVerification}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            toggleFilterModal={toggleFilterModal}
          />
        </div>
      </motion.main>
    </ErrorBoundary>
  );
};
export default Users;